import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../hooks/useAuth';
import {
  reviewApi,
  ReviewApiError,
  type MyReview,
  type ReviewDisplayNameMode,
} from '../../services/reviewApi';
import Button from '../ui/Button';

const MIN_LEN = 10;
const MAX_LEN = 1000;
const MAX_NAME_LEN = 40;

interface Props {
  activityId: string;
  /** When provided, the modal edits an existing review; otherwise it creates one. */
  existing?: MyReview | null;
  onClose: () => void;
  onSaved: (review: MyReview, mode: 'created' | 'updated') => void;
}

/**
 * Shared review composer used both for writing a new review (from a booking card)
 * and editing an existing one (from the "我的评论" tab). The activity detail page
 * itself is read-only and never mounts this.
 */
export default function ReviewEditorModal({ activityId, existing, onClose, onSaved }: Props) {
  const { t } = useTranslation('review');
  const { user } = useAuth();

  const [content, setContent] = useState(existing?.content ?? '');
  const [nameMode, setNameMode] = useState<ReviewDisplayNameMode>(
    existing?.displayNameMode ?? 'account'
  );
  const [customName, setCustomName] = useState(existing?.authorDisplayName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Empty custom name => '' so the preview line is hidden rather than lying.
  const previewName = (() => {
    if (nameMode === 'anonymous') return t('anonymousName');
    if (nameMode === 'custom') return customName.trim();
    return user?.name || t('fallbackName');
  })();

  const trimmedLen = content.trim().length;
  const customNameValid = nameMode !== 'custom' || customName.trim().length > 0;
  const canSubmit =
    trimmedLen >= MIN_LEN && trimmedLen <= MAX_LEN && customNameValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        content: content.trim(),
        displayNameMode: nameMode,
        customName: nameMode === 'custom' ? customName.trim() : undefined,
      };
      const saved = existing
        ? await reviewApi.update(existing.id, payload)
        : await reviewApi.create(activityId, payload);
      onSaved(saved, existing ? 'updated' : 'created');
    } catch (err) {
      setError(err instanceof ReviewApiError ? err.message : 'Error');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {existing ? t('editor.titleEdit') : t('writeCta')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={submitting}
            aria-label={t('editor.cancel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            maxLength={MAX_LEN}
            placeholder={t('editor.placeholder')}
            className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
            <span>{t('editor.piiHint')}</span>
            <span>
              {trimmedLen}/{MAX_LEN}
            </span>
          </div>
          {trimmedLen > 0 && trimmedLen < MIN_LEN && (
            <p className="text-xs text-red-600 mt-1">{t('editor.minHint', { min: MIN_LEN })}</p>
          )}

          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">{t('editor.namePrompt')}</p>
            <div className="flex flex-wrap gap-3 text-sm">
              {(['account', 'custom', 'anonymous'] as ReviewDisplayNameMode[]).map((mode) => (
                <label key={mode} className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="reviewNameMode"
                    checked={nameMode === mode}
                    onChange={() => setNameMode(mode)}
                    className="text-primary focus:ring-primary"
                  />
                  <span>{t(`editor.name${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}</span>
                </label>
              ))}
            </div>
            {nameMode === 'custom' && (
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                maxLength={MAX_NAME_LEN}
                placeholder={t('editor.customNamePlaceholder')}
                className="mt-2 w-full sm:w-64 rounded-lg border border-gray-300 p-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              />
            )}
            {previewName && (
              <p className="text-xs text-gray-500 mt-2">
                {t('editor.namePreview', { name: previewName })}
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
              {existing ? t('editor.submitUpdate') : t('editor.submitCreate')}
            </Button>
            <Button size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
              {t('editor.cancel')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
